import { AppManager } from "..";
import { QueryResultCallback } from "../../models/Callback/Callback";
import { DealerGroup } from "../../models/Organization/DealerGroup";
import { TDealerGroupAttr, TFullDealerGroupAttr, TOrgInvitation, TOrgMemberAttr } from "../../models/Organization/Types";
import { TUserAttr } from "../../models/User/User";
import { DealerGroupStoreActions } from "../../redux/services/dealerGroup/actions";
import { APIManager } from "../APIManager";
import { DGroupNetworkHelper } from "./NetworkHelper";

type TInviteUser = {
    email: string;
    names: string;
};
export class DealerGroupManager {
    networkHelper = DGroupNetworkHelper;
    static newOrgObj(data: TDealerGroupAttr) {
        return new DealerGroup(data);
    }
    getUserDGroup(orgId: string) {
        return DealerGroupStoreActions.getDealerGroups().find((t) => t.id === orgId);
    }
    static getActiveDGroup() {
        return DealerGroupStoreActions.getActiveDealerGroup();
    }
    async apiFetchMembers(org: DealerGroup, cb: QueryResultCallback<TOrgMemberAttr[]>) {
        try {
            const resp = await APIManager.get(APIManager.DEALERGROUP.MEMBERS_API(org.id));
            org.updateMembers(resp.data);
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }
    async apiUpdateBasicInfo(orgId: string, orgInfo: { name: string }, cb: QueryResultCallback<TDealerGroupAttr>) {
        try {
            const resp = await APIManager.put(APIManager.DEALERGROUP.BASIC_INFO_API(orgId), orgInfo);
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async activateDealerGroup(org: DealerGroup, cb: QueryResultCallback) {
        try {
            const allOrgAttrs = await APIManager.get(APIManager.DEALERGROUP.ALL_INFO_API(org.id));
            this.hydrateFullOrganization(allOrgAttrs.data, true);
            cb(null, allOrgAttrs);
        } catch (err) {
            cb(`${err}`);
        }
    }
    saveUserDealerGroup(orgs: TDealerGroupAttr[]) {
        DealerGroupStoreActions.saveDealerGroups(orgs.map((t) => this.updateOrgAttrOrAddNew(t, false)));
    }
    async refreshActiveDealerGroup(cb: QueryResultCallback = () => {}) {
        const activeOrg = DealerGroupStoreActions.getActiveDealerGroup();
        if (activeOrg) {
            await this.activateDealerGroup(activeOrg, cb);
            await this.apiFetchInvitations(activeOrg.id, () => {});
        }
    }
    getCurrentActiveDGroup() {
        return DealerGroupStoreActions.getActiveDealerGroup();
    }
    getPersistedActiveDGroup() {
        const savedOrgId = DealerGroupStoreActions.getSavedDGroupId();
        if (savedOrgId) {
            return DealerGroupStoreActions.getDealerGroups().find((t) => t.id === savedOrgId);
        }
        return null;
    }
    getUserDGroups() {
        return DealerGroupStoreActions.getDealerGroups();
    }

    async apiFetchInvitations(orgId: string, cb: QueryResultCallback<TOrgInvitation[]>) {
        try {
            const resp = await APIManager.get(APIManager.DEALERGROUP.INVITATIONS_API(orgId));
            const allDGroups = DealerGroupStoreActions.getDealerGroups();
            const dGroup = allDGroups.find((t) => t.id === orgId);
            if (dGroup) dGroup.updateInvitations(resp.data);
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiDeleteInvitation(orgId: string, invitationId: string, cb: QueryResultCallback<TOrgInvitation[]>) {
        try {
            await APIManager.delete(APIManager.DEALERGROUP.INVITATION_ITEM_API(orgId, invitationId));
            await this.apiFetchInvitations(orgId, cb);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiCreateRole(
        orgId: string,
        roleData: { name: string; scopes: string[]; setAsDefault?: boolean },
        cb: QueryResultCallback<TDealerGroupAttr>,
    ) {
        try {
            const result = await APIManager.post(APIManager.DEALERGROUP.ROLES_API(orgId), roleData);
            cb(null, result.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiUpdateOrgRole(
        orgId: string,
        roleId: string,
        roleData: { name: string; scopes: string[]; setAsDefault?: boolean },
        cb: QueryResultCallback<TDealerGroupAttr>,
    ) {
        try {
            const result = await APIManager.put(APIManager.DEALERGROUP.ROLE_ITEM_API(orgId, roleId), roleData);
            cb(null, result.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiDeleteRole(orgId: string, roleId: string, cb: QueryResultCallback<TDealerGroupAttr>) {
        try {
            const result = await APIManager.delete(APIManager.DEALERGROUP.ROLE_ITEM_API(orgId, roleId));
            cb(null, result.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiSetRoleDefault(orgId: string, roleId: string, cb: QueryResultCallback<TDealerGroupAttr>) {
        try {
            const result = await APIManager.patch(APIManager.DEALERGROUP.SET_DEFAULT_ROLE_API(orgId, roleId));
            cb(null, result.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiSendOrgInvitations(
        orgId: string,
        invites: TInviteUser[],
        cb: QueryResultCallback<{
            alreadyMembers: TInviteUser[];
            alreadySent: TInviteUser[];
            sentInvitations: TInviteUser[];
            failedToSend: TInviteUser[];
        }>,
    ) {
        try {
            const resp = await APIManager.post(APIManager.DEALERGROUP.INVITATIONS_API(orgId), invites);
            await this.apiFetchInvitations(orgId, () => {});
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiUpdateMemberRole(orgId: string, memberId: string, roleId: string, cb: QueryResultCallback<TDealerGroupAttr>) {
        try {
            const result = await APIManager.put(APIManager.DEALERGROUP.UPDATE_MEMBER_ROLE_API(orgId), { memberId, roleId });
            cb(null, result.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiDeleteMember(orgId: string, memberId: string, cb: QueryResultCallback<TDealerGroupAttr>) {
        try {
            const result = await APIManager.delete(APIManager.DEALERGROUP.MEMBER_ITEM_API(orgId, memberId));
            cb(null, result.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    hydrateFullOrganization(data: TFullDealerGroupAttr, activate = false) {
        const allOrgs = DealerGroupStoreActions.getDealerGroups();

        let org = allOrgs.find((t) => t.id === data._id);
        if (org) {
            org.updateAttributes(data);
        } else {
            org = DealerGroupManager.newOrgObj(data);
            DealerGroupStoreActions.saveDealerGroups([...allOrgs, org]);
        }
        const { members, orgRoles, organizations, ...attrs } = data;
        org.updateMembers(members);
        org.updateRoles(orgRoles);
        org.updateAttributes(attrs);
        AppManager.org.saveUserOrganizations((organizations || []).map((r) => r.org));
        org.updateOrganizations(organizations || []);

        if (activate) {
            DealerGroupStoreActions.setActiveDGroup(org);
        }

        return org;
    }

    updateActiveDGroupMemberUserAttr = (userAttr: TUserAttr) => {
        const activeOrg = DealerGroupStoreActions.getActiveDealerGroup();
        if (activeOrg) {
            activeOrg.updateMemberUserAttr(userAttr);
        }
    };

    updateDealerGroupAttr = (orgAttr: TDealerGroupAttr) => {
        const dGroups = DealerGroupStoreActions.getDealerGroups();
        for (const org of dGroups) {
            if (orgAttr._id === org.id) {
                org.updateAttributes(orgAttr);
            }
        }

        DealerGroupStoreActions.saveDealerGroups(dGroups);
    };

    updateOrgAttrOrAddNew(orgAttr: TDealerGroupAttr, saveToStore = true) {
        let org = this.getUserDGroup(orgAttr._id);
        if (org) {
            org.updateAttributes(orgAttr);
        } else {
            org = DealerGroupManager.newOrgObj(orgAttr);
        }
        if (saveToStore) DealerGroupStoreActions.addOrUpdateToDealerGroups(org);
        return org;
    }
}
